import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, Trophy, Trash2, Edit, Play, Archive, ArchiveRestore, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ParticipantsTable from './ParticipantsTable';
import WinnerSelectionModal from './WinnerSelectionModal';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import LiveDrawAdminModal from './LiveDrawAdminModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DrawsTable = ({ draws, onRefresh, showArchived, onToggleArchived }) => {
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showWinnerSelection, setShowWinnerSelection] = useState(false);
  const [showLiveDraw, setShowLiveDraw] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [drawToDelete, setDrawToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const [editingField, setEditingField] = useState(null); // {drawId: 'xxx', field: 'title'}
  const [editValue, setEditValue] = useState('');

  const archiveDraw = async (draw) => {
    setArchiving(draw.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/draws/${draw.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(draw.archived ? 'Draw unarchived' : 'Draw archived');
      onRefresh();
    } catch (error) {
      console.error('Archive error:', error);
      toast.error('Failed to archive draw');
    } finally {
      setArchiving(null);
    }
  };

  const viewParticipants = async (draw) => {
    setSelectedDraw(draw);
    setLoadingParticipants(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/draws/${draw.id}/participants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParticipants(response.data);
      setShowParticipants(true);
    } catch (error) {
      toast.error('Failed to load participants');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const selectWinner = (draw) => {
    setSelectedDraw(draw);
    setShowWinnerSelection(true);
  };

  const startLiveDraw = (draw) => {
    setSelectedDraw(draw);
    setShowLiveDraw(true);
  };

  const handleWinnerSelected = () => {
    setShowWinnerSelection(false);
    setShowLiveDraw(false);
    onRefresh();
  };

  const openDeleteDialog = (draw) => {
    setDrawToDelete(draw);
    setShowDeleteDialog(true);
  };

  const startEditing = (drawId, field, currentValue) => {
    setEditingField({ drawId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveEdit = async (drawId) => {
    if (!editingField) return;
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        [editingField.field]: editValue
      };
      
      await axios.put(
        `${API}/admin/draws/${drawId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Draw updated successfully');
      setEditingField(null);
      setEditValue('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update draw');
    }
  };


  const deleteDraw = async () => {
    if (!drawToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/draws/${drawToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Draw deleted successfully');
      setShowDeleteDialog(false);
      setDrawToDelete(null);
      onRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete draw');
    } finally {
      setDeleting(false);
    }
  };

  const isEditing = (drawId, field) => editingField?.drawId === drawId && editingField?.field === field;

  const renderEditable = (draw, field, display, { inputType = 'text', toEditValue = (v) => v || '', className = '' } = {}) => {
    const editable = draw.status === 'pending';
    if (isEditing(draw.id, field)) {
      return (
        <div className="flex items-center gap-2">
          <input
            type={inputType}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit(draw.id);
              if (e.key === 'Escape') cancelEditing();
            }}
            className="w-full min-w-0 rounded-lg border border-[#EAB308] bg-[#09090b] px-2 py-1 text-sm text-white focus:outline-none"
            autoFocus
            data-testid={`edit-${field}-input-${draw.id}`}
          />
          <button onClick={() => saveEdit(draw.id)} className="text-[#10b981]" aria-label="Save"><Check className="h-4 w-4" /></button>
          <button onClick={cancelEditing} className="text-[#f43f5e]" aria-label="Cancel"><X className="h-4 w-4" /></button>
        </div>
      );
    }
    return (
      <div
        onClick={() => editable && startEditing(draw.id, field, toEditValue(draw[field]))}
        className={`group inline-flex max-w-full items-center gap-1.5 ${editable ? 'tap-none cursor-pointer' : ''} ${className}`}
        data-testid={`edit-${field}-trigger-${draw.id}`}
      >
        <span className="min-w-0 break-words">{display}</span>
        {editable && <Edit className="h-3 w-3 shrink-0 text-[#52525b] md:opacity-0 md:group-hover:opacity-100" />}
      </div>
    );
  };

  const statusBadge = (draw) => {
    if (draw.status === 'completed') return <Badge className="bg-[#EAB308] text-black hover:bg-[#EAB308]">Completed</Badge>;
    if (draw.status === 'countdown') return <Badge className="animate-pulse bg-[#f43f5e] text-white hover:bg-[#f43f5e]">LIVE</Badge>;
    if (draw.status === 'spinning') return <Badge className="animate-pulse bg-[#f43f5e] text-white hover:bg-[#f43f5e]">SPINNING</Badge>;
    return <Badge className="bg-[#A855F7]/20 text-[#c084fc] hover:bg-[#A855F7]/20">Pending</Badge>;
  };

  const prizeDisplay = (draw) => (
    <>
      <span className="font-semibold text-[#EAB308]">{draw.prize_description}</span>
      {draw.bonus_amount > 0 && (
        <span className="ml-1 text-sm font-bold" style={{ color: '#39FF14', textShadow: '0 0 8px rgba(57,255,20,0.5)' }}>+ £{draw.bonus_amount}</span>
      )}
    </>
  );

  const dateToLocalInput = (v) => new Date(v).toISOString().slice(0, 16);

  const actions = (draw, compact = false) => (
    <div className={`flex items-center gap-2 ${compact ? 'flex-wrap' : ''}`}>
      <Button
        data-testid={`view-participants-btn-${draw.id}`}
        onClick={() => viewParticipants(draw)}
        variant="outline"
        size="sm"
        className="h-9 rounded-full border-[#06b6d4]/50 text-[#06b6d4] hover:bg-[#06b6d4] hover:text-black"
      >
        <Eye className="mr-1 h-4 w-4" />
        {compact ? 'Entrants' : 'View'}
      </Button>
      {draw.status === 'pending' && (
        <Button data-testid={`start-live-draw-btn-${draw.id}`} onClick={() => startLiveDraw(draw)} size="sm" className="h-9 rounded-full bg-[#A855F7] text-white hover:bg-[#9333EA]">
          <Play className="mr-1 h-4 w-4" />
          Live Draw
        </Button>
      )}
      {(draw.status === 'countdown' || draw.status === 'spinning') && (
        <Button data-testid={`continue-live-draw-btn-${draw.id}`} onClick={() => startLiveDraw(draw)} size="sm" className="h-9 animate-pulse rounded-full bg-[#EAB308] text-black hover:bg-[#CA8A04]">
          <Play className="mr-1 h-4 w-4" />
          Continue Live
        </Button>
      )}
      {draw.status === 'completed' && (
        <Button
          data-testid={`archive-draw-btn-${draw.id}`}
          onClick={(e) => { e.stopPropagation(); archiveDraw(draw); }}
          variant="outline"
          size="sm"
          disabled={archiving === draw.id}
          className={`h-9 w-9 rounded-full p-0 ${draw.archived ? 'border-[#10b981]/50 text-[#10b981] hover:bg-[#10b981]' : 'border-white/[0.1] text-[#a1a1aa] hover:bg-[#52525b]'} hover:text-white`}
          title={draw.archived ? 'Unarchive' : 'Archive'}
        >
          {draw.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </Button>
      )}
      <Button
        data-testid={`delete-draw-btn-${draw.id}`}
        onClick={(e) => { e.stopPropagation(); openDeleteDialog(draw); }}
        variant="outline"
        size="sm"
        className={`h-9 w-9 rounded-full border-[#f43f5e]/50 p-0 text-[#f43f5e] hover:bg-[#f43f5e] hover:text-white ${compact ? 'ml-auto' : ''}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-5">
      <div className="min-w-0">
        <h2 className="font-heading text-[15px] font-semibold text-white">All draws</h2>
        <p className="text-xs text-[#a1a1aa]">{draws.length} draw{draws.length === 1 ? '' : 's'} · tap a field to edit</p>
      </div>
      <Button
        onClick={onToggleArchived}
        data-testid="toggle-archived-btn"
        variant="outline"
        size="sm"
        className={`h-9 shrink-0 rounded-full ${showArchived ? 'border-[#EAB308] text-[#EAB308]' : 'border-white/[0.1] text-[#a1a1aa]'} hover:text-white`}
      >
        <Archive className="mr-2 h-4 w-4" />
        {showArchived ? 'Hide archived' : 'Show archived'}
      </Button>
    </div>
  );

  if (draws.length === 0) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-[#18181b]">
        {header}
        <div className="border-t border-white/[0.06] px-6 py-12 text-center">
          <Trophy className="mx-auto mb-3 h-10 w-10 text-[#52525b]" />
          <h3 className="font-heading text-base font-semibold text-white">No draws yet</h3>
          <p className="mt-1 text-sm text-[#a1a1aa]">{showArchived ? 'Nothing archived.' : 'Create your first giveaway to get started.'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#18181b]">
        {header}

        {/* Mobile cards */}
        <div className="divide-y divide-white/[0.06] border-t border-white/[0.06] md:hidden" data-testid="draws-mobile-list">
          {draws.map((draw) => (
            <div key={draw.id} className={`p-4 ${draw.archived ? 'opacity-60' : ''}`} data-testid={`draw-card-${draw.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {renderEditable(draw, 'title', draw.title, { className: 'font-heading text-[15px] font-semibold text-white' })}
                  <div className="mt-0.5 text-xs text-[#a1a1aa]">
                    {renderEditable(draw, 'description', draw.description, { className: 'line-clamp-2' })}
                  </div>
                </div>
                <div className="shrink-0">{statusBadge(draw)}</div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl bg-[#09090b]/60 p-3 text-sm">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#71717a]">Prize</p>
                  <div className="mt-0.5">{renderEditable(draw, 'prize_description', prizeDisplay(draw))}</div>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#71717a]">Draw date</p>
                  <div className="mt-0.5 text-[#d4d4d8]">
                    {renderEditable(draw, 'draw_date', new Date(draw.draw_date).toLocaleDateString(), { inputType: 'datetime-local', toEditValue: dateToLocalInput })}
                  </div>
                </div>
                {draw.winner_name && (
                  <div className="col-span-2 flex items-center gap-2 border-t border-white/[0.06] pt-2.5">
                    <Trophy className="h-4 w-4 text-[#EAB308]" />
                    <span className="font-medium text-white">{draw.winner_name}</span>
                    <span className="text-xs text-[#a1a1aa]">· Ticket #{draw.winning_ticket}</span>
                  </div>
                )}
              </div>
              <div className="mt-3">{actions(draw, true)}</div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead className="bg-white/[0.03]">
              <tr>
                {['Title', 'Prize', 'Draw Date', 'Status', 'Winner', 'Actions'].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {draws.map((draw) => (
                <motion.tr
                  key={draw.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`transition-colors hover:bg-white/[0.02] ${draw.archived ? 'opacity-60' : ''}`}
                  data-testid={`draw-row-${draw.id}`}
                >
                  <td className="max-w-xs px-5 py-4">
                    {renderEditable(draw, 'title', draw.title, { className: 'font-semibold text-white' })}
                    <div className="text-sm text-[#a1a1aa]">{renderEditable(draw, 'description', draw.description, { className: 'line-clamp-1' })}</div>
                  </td>
                  <td className="px-5 py-4">{renderEditable(draw, 'prize_description', prizeDisplay(draw))}</td>
                  <td className="px-5 py-4 text-[#a1a1aa]">
                    {renderEditable(draw, 'draw_date', new Date(draw.draw_date).toLocaleDateString(), { inputType: 'datetime-local', toEditValue: dateToLocalInput })}
                  </td>
                  <td className="px-5 py-4">{statusBadge(draw)}</td>
                  <td className="px-5 py-4">
                    {draw.winner_name ? (
                      <div>
                        <div className="flex items-center gap-2 font-semibold text-white"><Trophy className="h-4 w-4 text-[#EAB308]" />{draw.winner_name}</div>
                        <div className="text-xs text-[#a1a1aa]">Ticket #{draw.winning_ticket}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-[#52525b]">-</span>
                    )}
                  </td>
                  <td className="px-5 py-4">{actions(draw)}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Participants Modal */}
      {showParticipants && (
        <ParticipantsTable
          draw={selectedDraw}
          participants={participants}
          onClose={() => setShowParticipants(false)}
          onRefresh={() => viewParticipants(selectedDraw)}
        />
      )}

      {/* Winner Selection Modal */}
      {showWinnerSelection && (
        <WinnerSelectionModal
          draw={selectedDraw}
          onClose={() => setShowWinnerSelection(false)}
          onWinnerSelected={handleWinnerSelected}
        />
      )}

      {/* Live Draw Admin Modal */}
      {showLiveDraw && (
        <LiveDrawAdminModal
          draw={selectedDraw}
          onClose={() => setShowLiveDraw(false)}
          onComplete={handleWinnerSelected}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDrawToDelete(null);
        }}
        onConfirm={deleteDraw}
        loading={deleting}
        title="Delete Draw?"
        message={`Are you sure you want to delete "${drawToDelete?.title}"? The draw will be removed but user ticket history will be preserved for their records. Notifications will be deleted.`}
      />
    </>
  );
};

export default DrawsTable;
